import type { AccountEventType } from "@storyboard/web-client";
import { getWorkspaceConfig } from "@/data/workspaceConfig";

/** How a workspace's read receipts clear unreads: a watermark per feed, or one item at a time. */
export type ReadModel = "watermark" | "perItem";

/**
 * The read model a workspace runs on: per item where the Dispatch page is on, the watermark everywhere else.
 *
 * The gate is the one `useDispatchEnabled` reads, `web.dispatch.enabled`; a workspace whose config has not loaded reads
 * as the watermark.
 */
export function readModelForWorkspace(workspaceId: string): ReadModel {
  return getWorkspaceConfig(workspaceId)?.web?.dispatch?.enabled === true ? "perItem" : "watermark";
}

/**
 * The account events that mark a whole feed read under the watermark model: everything created at or before the event
 * is read.
 *
 * This is the read model from before the Dispatch page, for every role: seeing a message, opening the channel, or
 * listening reads everything that arrived before it. "Read Feed Item" is not one of them; only the Dispatch page sends
 * it.
 */
export const WATERMARK_CLEAR_EVENTS: readonly AccountEventType[] = [
  "Saw Feed Item",
  "Marked Feed as Read",
  "Opened Feed",
  "Started Listening to Feed Item",
];

/**
 * The account events that mark a whole feed read under the per-item model: everything created at or before the event
 * is read.
 *
 * "Opened Feed" is not one of them: a channel opening is not a reading of every message in it. This list and the two
 * below are the per-item model, which only Dispatch-enabled workspaces run ({@link readModelForWorkspace});
 * `calculateUnreads.tsx` applies them, and `web/src/hooks/dispatch/dispatchModel.ts` explains why the model is shaped
 * this way alongside the Dispatch page's other decisions.
 */
export const FEED_CLEAR_EVENTS: readonly AccountEventType[] = ["Marked Feed as Read"];

/**
 * The account events that mark one item read under the per-item model, the item they name and nothing else, for every
 * role.
 */
export const ITEM_READ_EVENTS: readonly AccountEventType[] = ["Saw Feed Item", "Read Feed Item"];

/**
 * The per-item reads that count for a driver only: playing a message reads it for the driver who heard it, but a
 * dispatcher listening is too weak a signal to count as having handled the message.
 */
export const DRIVER_ITEM_READ_EVENTS: readonly AccountEventType[] = ["Started Listening to Feed Item"];

/** Every event that can change an unread flag under either model, so an ingest knows when to recompute. */
export const unreadClearEvents: AccountEventType[] = [
  ...new Set([...WATERMARK_CLEAR_EVENTS, ...FEED_CLEAR_EVENTS, ...ITEM_READ_EVENTS, ...DRIVER_ITEM_READ_EVENTS]),
];
